/* eslint-disable react/jsx-pascal-case */
/* Original name @uswitch/trustyle.card */

import * as React from 'react'
import { Themed } from 'theme-ui'
import ImgixImg from '../imgix-img'
import { ButtonLink } from '../button-link'
import { Link } from '@uswitch/ev-core/react'
import dayjs from '@uswitch/ev-utils/trustyle-date'
import { DynamicSparkIcon } from '../spark-icons/dynamic-spark-icon'
import { ConditionalWrapper } from '@uswitch/ev-utils/conditional-wrapper'

const makeStyles = variant => element =>
  `compounds.card.variants.${variant}${element ? `.${element}` : ''}`

const Card = ({
  className = '',
  critical = false,
  date,
  description,
  imageSize = 'cover',
  imgAlt,
  imgSizes = '768px',
  imgSrc,
  imageProps = {},
  linkHref,
  linkIcon,
  linkText,
  superScript,
  tag,
  title,
  variant = 'vertical',
  headerChildren,
  contentChildren,
  button,
  sys,
  name,
}) => {
  const styles = makeStyles(variant)
  const isAspectRatioBox = variant === 'vertical' || variant === 'horizontal'

  const HeaderWrapper = children => (
    <Choose>
      <When condition={headerChildren}>
        <div sx={{ variant: styles('headerChildren') }}>
          <div sx={{ variant: styles('headerChildren') }}>{headerChildren}</div>
          <span>{children}</span>
        </div>
      </When>

      <Otherwise>{children}</Otherwise>
    </Choose>
  )

  const AspectRatioWrapper = children => (
    <Choose>
      <When condition={isAspectRatioBox}>
        <div sx={{ variant: styles('aspectRatioWrapper') }}>
          <ConditionalWrapper
            condition={linkHref}
            wrapper={Link}
            sx={{ variant: styles('image') }}
            otherwise={({ children, ...props }) => <span {...props}>{children}</span>}
            href={linkHref}
            sys={sys}
            name={name}
          >
            <div sx={{ variant: styles('aspectRatioBox') }}>
              <div sx={{ variant: styles('cardImage'), width: '100%' }}>{children}</div>
            </div>
          </ConditionalWrapper>
        </div>
      </When>

      <Otherwise>
        <ConditionalWrapper
          condition={linkHref}
          wrapper={Link}
          otherwise={({ children, ...props }) => <span {...props}>{children}</span>}
          sx={{ variant: styles('image') }}
          href={linkHref}
          sys={sys}
          name={name}
        >
          {children}
        </ConditionalWrapper>
      </Otherwise>
    </Choose>
  )

  const journeyVariant = variant === 'journey-card'

  const JourneyButton = (
    <ButtonLink
      variant='primary-journey'
      href={linkHref}
      afterIcon={linkIcon}
      sys={sys}
      name={name}
    >
      {linkText}
    </ButtonLink>
  )

  return (
    <div className={className} sx={{ variant: styles() }}>
      {HeaderWrapper(
        AspectRatioWrapper(
          <ImgixImg
            alt={imgAlt}
            src={imgSrc}
            sizes={imgSizes}
            critical={critical}
            ctfToUswitchContenftul={false}
            clearPx={false}
            imgixAutoFormatCompress={false}
            svgAsImg={false}
            {...imageProps}
            imgixParams={{
              fit: imageSize === 'cover' ? 'crop' : 'fill',
              crop: 'faces,entropy',
              ar: '16:9',
              fill: 'solid',
              ...(imageProps.imgixParams || {}),
            }}
          />,
        ),
      )}

      <div
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1',
          variant: styles('content'),
        }}
      >
        <div
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            variant: styles('meta'),
          }}
        >
          {date && (
            <Themed.p as='div' sx={{ variant: styles('date') }}>
              {dayjs(date).format('DD MMMM YYYY')}
            </Themed.p>
          )}

          {tag && <span sx={{ marginRight: 'sm', variant: styles('tag') }}>{tag}</span>}

          {superScript && (
            <Themed.p as='div' sx={{ variant: styles('superScript') }}>
              <p>{superScript}</p>
            </Themed.p>
          )}

          {contentChildren && (
            <div sx={{ variant: styles('contentChildren') }}>{contentChildren}</div>
          )}
        </div>
        {title && (
          <Themed.h3 sx={{ margin: '0', variant: styles('heading') }}>
            <ConditionalWrapper
              condition={linkHref}
              wrapper={Link}
              otherwise={({ children, ...props }) => <span {...props}>{children}</span>}
              href={linkHref}
              sys={sys}
              name={name}
            >
              {title}
            </ConditionalWrapper>
          </Themed.h3>
        )}
        {description && <Themed.p sx={{ variant: styles('description') }}>{description}</Themed.p>}
        {linkText && !journeyVariant && (
          <ConditionalWrapper
            condition={linkHref}
            wrapper={Link}
            otherwise={({ children, ...props }) => <span {...props}>{children}</span>}
            sx={{ textDecoration: 'underline', variant: styles('link') }}
            href={linkHref}
            sys={sys}
            name={name}
          >
            {linkIcon && <DynamicSparkIcon glyph={linkIcon} color='brand' />}
            {linkText}
          </ConditionalWrapper>
        )}
        {linkText && journeyVariant && ((button && button) || JourneyButton)}
      </div>
    </div>
  )
}

export default Card
